'use client';
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { supabase } from '@/lib/supabase';

interface Review {
  id: string;
  name: string;
  location: string;
  rating: number;
  review_text: string;
  package: string;
  date_label: string;
  created_at: string;
}

interface FormState {
  name: string;
  email: string;
  location: string;
  rating: number;
  review_text: string;
  package: string;
}

const PACKAGES = ['Basic Bundle', 'Premium Bundle', 'Luxe Package', 'Other / Not Sure'];

const StarPicker = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '28px',
          color: star <= value ? '#00f5ff' : 'rgba(0,245,255,0.2)',
          textShadow: star <= value ? '0 0 10px rgba(0,245,255,0.6)' : 'none',
          transition: 'color 0.15s ease, text-shadow 0.15s ease',
          padding: '0 2px',
          lineHeight: 1,
        }}
        aria-label={`${star} star${star !== 1 ? 's' : ''}`}
      >
        ★
      </button>
    ))}
  </div>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    location: '',
    rating: 5,
    review_text: '',
    package: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  async function fetchReviews() {
    setLoadingReviews(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('reviews')
        .select('id, name, location, rating, review_text, package, date_label, created_at')
        .eq('is_approved', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (!fetchError && data) {
        setReviews(data);
      }
    } catch {
      // silently fail — empty state shown
    } finally {
      setLoadingReviews(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.review_text.trim()) {
      setError('Please fill in your name and review.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: insertError } = await supabase.from('reviews').insert({
        name: form.name.trim(),
        email: form.email.trim(),
        location: form.location.trim(),
        rating: form.rating,
        review_text: form.review_text.trim(),
        package: form.package,
        date_label: 'Early Access Member',
        is_approved: false,
        sort_order: 999,
      });
      if (insertError) throw insertError;
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(0,245,255,0.15)',
    color: '#e0e8ff',
    padding: '12px 16px',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
    fontFamily: 'Barlow Condensed, Arial, sans-serif',
    fontWeight: 600,
    letterSpacing: '0.04em',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.2s ease',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Barlow Condensed, Arial, sans-serif',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.15em',
    color: 'rgba(0,245,255,0.6)',
    display: 'block',
    marginBottom: '6px',
  };

  return (
    <div style={{ background: '#050510', minHeight: '100vh' }}>
      <Header />

      {/* Page header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0d0d2b 0%, #050510 100%)',
          borderBottom: '1px solid rgba(0,245,255,0.15)',
          padding: 'clamp(36px, 6vw, 72px) clamp(16px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(rgba(0,245,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,245,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'absolute', top: '30%', right: '10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(123,47,255,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto relative" style={{ zIndex: 1 }}>
          <div
            className="inline-block px-3 py-1 mb-4"
            style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.3)' }}
          >
            <span style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#00f5ff' }}>
              Early Access Reviews
            </span>
          </div>
          <h1
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(36px, 8vw, 72px)',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: '#e0e8ff',
              marginBottom: '16px',
              textShadow: '0 0 40px rgba(0,245,255,0.15)',
            }}
          >
            CUSTOMER
            <br />
            <span style={{ color: '#00f5ff', textShadow: '0 0 30px rgba(0,245,255,0.5)' }}>REVIEWS</span>
          </h1>
          <p
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 600,
              fontSize: 'clamp(14px, 2.5vw, 18px)',
              color: '#7b8ab8',
              letterSpacing: '0.06em',
              maxWidth: '520px',
            }}
          >
            On the waitlist or booked in advance? Share your experience with our customer service and website — your feedback helps us launch strong.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">

          {/* ── Submit Review Form ── */}
          <div>
            <div className="mb-6">
              <h2
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(22px, 4vw, 30px)',
                  textTransform: 'uppercase',
                  color: '#e0e8ff',
                  letterSpacing: '0.04em',
                  marginBottom: '6px',
                }}
              >
                Leave a Review
              </h2>
              <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 600, fontSize: '14px', color: '#7b8ab8', letterSpacing: '0.04em' }}>
                Reviews are reviewed before publishing. Waitlist &amp; advance booking members only.
              </p>
            </div>

            {submitted ? (
              <div
                style={{
                  background: 'rgba(0,245,255,0.06)',
                  border: '1px solid rgba(0,245,255,0.3)',
                  padding: '32px 24px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>✓</div>
                <p
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '20px',
                    textTransform: 'uppercase',
                    color: '#00f5ff',
                    letterSpacing: '0.08em',
                    marginBottom: '8px',
                    textShadow: '0 0 12px rgba(0,245,255,0.4)',
                  }}
                >
                  Review Submitted!
                </p>
                <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 600, fontSize: '14px', color: '#7b8ab8' }}>
                  Thank you — your review will appear once approved.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Name */}
                <div>
                  <label style={labelStyle}>Your Name *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="First name or full name"
                    required
                    style={inputStyle}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)')}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email (optional — not published)</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    style={inputStyle}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)')}
                  />
                </div>

                {/* Location */}
                <div>
                  <label style={labelStyle}>City / Location (optional)</label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. Mesquite, NV"
                    style={inputStyle}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)')}
                  />
                </div>

                {/* Package */}
                <div>
                  <label style={labelStyle}>Package / Service</label>
                  <select
                    name="package"
                    value={form.package}
                    onChange={handleChange}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)')}
                  >
                    <option value="" style={{ background: '#0d0d2b' }}>Select a package (optional)</option>
                    {PACKAGES.map((p) => (
                      <option key={p} value={p} style={{ background: '#0d0d2b' }}>{p}</option>
                    ))}
                  </select>
                </div>

                {/* Rating */}
                <div>
                  <label style={labelStyle}>Rating *</label>
                  <StarPicker value={form.rating} onChange={(v) => setForm((prev) => ({ ...prev, rating: v }))} />
                </div>

                {/* Review text */}
                <div>
                  <label style={labelStyle}>Your Review *</label>
                  <textarea
                    name="review_text"
                    value={form.review_text}
                    onChange={handleChange}
                    placeholder="Tell us about your experience with our customer service, booking process, or website..."
                    required
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)')}
                  />
                </div>

                {error && (
                  <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 700, fontSize: '13px', color: '#ff4466', letterSpacing: '0.04em' }}>
                    ⚠ {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    fontFamily: 'Barlow Condensed, Arial Narrow, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#050510',
                    background: submitting ? 'rgba(0,245,255,0.5)' : '#00f5ff',
                    border: '2px solid #00f5ff',
                    padding: '14px 28px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    width: '100%',
                    transition: 'background 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: '0 0 16px rgba(0,245,255,0.3)',
                  }}
                  onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.boxShadow = '0 0 28px rgba(0,245,255,0.6)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 16px rgba(0,245,255,0.3)'; }}
                >
                  {submitting ? 'Submitting...' : '★ Submit Review'}
                </button>
              </form>
            )}
          </div>

          {/* ── Published Reviews ── */}
          <div>
            <div className="mb-6">
              <h2
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(22px, 4vw, 30px)',
                  textTransform: 'uppercase',
                  color: '#e0e8ff',
                  letterSpacing: '0.04em',
                  marginBottom: '6px',
                }}
              >
                What People Are Saying
              </h2>
              <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 600, fontSize: '14px', color: '#7b8ab8', letterSpacing: '0.04em' }}>
                Verified early access members only.
              </p>
            </div>

            {loadingReviews ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      background: 'rgba(0,245,255,0.03)',
                      border: '1px solid rgba(0,245,255,0.08)',
                      padding: '20px',
                      height: '120px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div
                style={{
                  background: 'rgba(0,245,255,0.03)',
                  border: '1px solid rgba(0,245,255,0.1)',
                  padding: '48px 24px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.4 }}>★</div>
                <p
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '16px',
                    textTransform: 'uppercase',
                    color: 'rgba(224,232,255,0.4)',
                    letterSpacing: '0.1em',
                    marginBottom: '6px',
                  }}
                >
                  No Reviews Yet
                </p>
                <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 600, fontSize: '13px', color: 'rgba(224,232,255,0.25)', letterSpacing: '0.04em' }}>
                  Be the first to leave a review.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,245,255,0.04) 0%, rgba(123,47,255,0.04) 100%)',
                      border: '1px solid rgba(0,245,255,0.12)',
                      padding: '20px',
                      position: 'relative',
                    }}
                  >
                    {/* Stars */}
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '10px' }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span
                          key={s}
                          style={{
                            fontSize: '14px',
                            color: s <= review.rating ? '#00f5ff' : 'rgba(0,245,255,0.15)',
                            textShadow: s <= review.rating ? '0 0 6px rgba(0,245,255,0.5)' : 'none',
                          }}
                        >
                          ★
                        </span>
                      ))}
                    </div>

                    {/* Review text */}
                    <p
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#c0cce8',
                        lineHeight: 1.5,
                        letterSpacing: '0.02em',
                        marginBottom: '14px',
                        fontStyle: 'italic',
                      }}
                    >
                      &ldquo;{review.review_text}&rdquo;
                    </p>

                    {/* Author */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                      <div>
                        <span
                          style={{
                            fontFamily: 'Barlow Condensed, Arial, sans-serif',
                            fontWeight: 900,
                            fontSize: '13px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: '#e0e8ff',
                          }}
                        >
                          {review.name}
                        </span>
                        {review.location && (
                          <span
                            style={{
                              fontFamily: 'Barlow Condensed, Arial, sans-serif',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#7b8ab8',
                              letterSpacing: '0.06em',
                              marginLeft: '6px',
                            }}
                          >
                            · {review.location}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {review.package && (
                          <span
                            style={{
                              fontFamily: 'Barlow Condensed, Arial, sans-serif',
                              fontWeight: 700,
                              fontSize: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.12em',
                              color: '#7b2fff',
                              background: 'rgba(123,47,255,0.12)',
                              border: '1px solid rgba(123,47,255,0.3)',
                              padding: '2px 8px',
                            }}
                          >
                            {review.package}
                          </span>
                        )}
                        <span
                          style={{
                            fontFamily: 'Barlow Condensed, Arial, sans-serif',
                            fontWeight: 700,
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: 'rgba(0,245,255,0.4)',
                          }}
                        >
                          {review.date_label || 'Early Access Member'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
